import { Button, Divider, TextField, Typography } from '@tunasong/ui-lib'
import type { ModelMessage } from 'ai'
import type { CSSProperties, FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { generatePromptSpec } from '../generate-prompt.js'
import type { AIContext, AIContextSettings } from '../schema/ai-schema.js'
import type { AIProvider } from '../schema/providers.js'

/** Extract value from an input change event without depending on DOM types */
const getInputValue = (e: unknown) => (e as { target: { value: string } }).target.value

const defaultSamplePrompts: Record<AIContext, string> = {
  global: 'Write a short creative greeting for a musician',
  'song:section': 'Write a verse about walking in the rain',
  'song:line': 'Continue this line: The morning light...',
  'song:image': 'Create an image for a song about the ocean at sunset',
  'song:music': 'Describe music for an upbeat summer song',
  advisor: 'What chord progression works well for a ballad?',
}

type PreviewMode = 'text' | 'image' | 'music'

const contextPreviewMode: Record<AIContext, PreviewMode> = {
  global: 'text',
  'song:section': 'text',
  'song:line': 'text',
  'song:image': 'image',
  'song:music': 'music',
  advisor: 'text',
}

export interface AIConfigPreviewProps {
  context: AIContext
  settings: AIContextSettings
  /** Streaming text result (used for text preview and image/music description step) */
  textResult?: { text: string; pending: boolean } | null
  /** URL of a generated preview image (for song:image context) */
  imageUrl?: string | null
  /** Whether image generation is in progress */
  imagePending?: boolean
  /** URL of a generated preview music clip (for song:music context) */
  musicUrl?: string | null
  /** Whether music generation is in progress */
  musicPending?: boolean
  /** Generate text (streaming completion) */
  onGenerateText: (params: { provider: AIProvider; model: string; prompt: ModelMessage[]; requestId: string }) => void
  /** Generate an image from a description prompt (for song:image context) */
  onGenerateImage?: (params: { description: string; model: string }) => void
  /** Generate a music clip from a description prompt (for song:music context) */
  onGenerateMusic?: (params: { description: string; model: string }) => void
  /** Called when context changes so consumer can clear stale preview state */
  onContextChange?: () => void
}

export const AIConfigPreview: FC<AIConfigPreviewProps> = props => {
  const {
    context,
    settings,
    textResult,
    imageUrl,
    imagePending,
    musicUrl,
    musicPending,
    onGenerateText,
    onGenerateImage,
    onGenerateMusic,
    onContextChange,
  } = props

  const mode = contextPreviewMode[context]
  const [samplePrompt, setSamplePrompt] = useState(defaultSamplePrompts[context])
  const [showPromptPreview, setShowPromptPreview] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Local copy of the text result, reset on each new generation to avoid stale data
  const [localResult, setLocalResult] = useState<{ text: string; pending: boolean } | null>(null)
  const [localPending, setLocalPending] = useState(false)

  // Reset sample prompt and notify consumer when context changes
  useEffect(() => {
    setSamplePrompt(defaultSamplePrompts[context])
    setError(null)
    setLocalResult(null)
    setLocalPending(false)
    onContextChange?.()
  }, [context, onContextChange])

  // Sync incoming textResult to local state (only forward, never stale)
  useEffect(() => {
    if (textResult != null) {
      setLocalResult(textResult)
      if (!textResult.pending) {
        setLocalPending(false)
      }
    }
  }, [textResult])

  // Track the resolved model from the last text generation for use in image generation
  const [resolvedModel, setResolvedModel] = useState<string | null>(null)

  // Ref to prevent double-triggering auto image/music generation
  const autoTriggeredRef = useRef(false)

  const handleGenerate = () => {
    setError(null)
    setLocalResult(null)
    setLocalPending(true)
    autoTriggeredRef.current = false
    try {
      const spec = generatePromptSpec({
        context,
        userConfig: settings,
        promptText: samplePrompt,
      })
      setResolvedModel(spec.model)
      onGenerateText({
        provider: spec.provider,
        model: spec.model,
        prompt: spec.prompt,
        requestId: `preview-${Date.now()}`,
      })
    } catch (e) {
      setLocalPending(false)
      setError(e instanceof Error ? e.message : 'Failed to generate prompt')
    }
  }

  // Build the prompt preview messages (best-effort, don't throw)
  let previewMessages: ModelMessage[] = []
  try {
    const spec = generatePromptSpec({
      context,
      userConfig: settings,
      promptText: samplePrompt || 'test',
    })
    previewMessages = spec.prompt
  } catch {
    // Ignore — preview is informational
  }

  const isTextPending = localPending || (localResult?.pending ?? false)
  const textDone = localResult != null && !localResult.pending && localResult.text.length > 0

  // Auto-trigger image/music generation when the text description completes
  useEffect(() => {
    if (autoTriggeredRef.current || !textDone || !resolvedModel || !localResult?.text) {
      return
    }
    if (mode === 'image' && onGenerateImage) {
      autoTriggeredRef.current = true
      onGenerateImage({ description: localResult.text, model: resolvedModel })
    } else if (mode === 'music' && onGenerateMusic) {
      autoTriggeredRef.current = true
      onGenerateMusic({ description: localResult.text, model: resolvedModel })
    }
  }, [textDone, resolvedModel, localResult?.text, mode, onGenerateImage, onGenerateMusic])

  const generateLabel =
    mode === 'image' ? 'Generate Image' : mode === 'music' ? 'Generate Music Clip' : 'Generate Preview'

  const isAssetMode = mode === 'image' || mode === 'music'
  const [showDescription, setShowDescription] = useState(false)

  const linkButtonStyle: CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--tuna-palette-primary-main)',
    fontSize: '0.75rem',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  }

  const boxBorderStyle: CSSProperties = {
    border: '1px solid var(--tuna-palette-divider)',
    borderRadius: 4,
    marginBottom: 16,
  }

  return (
    <div style={{ marginTop: 24 }}>
      <Divider style={{ marginBottom: 16 }} />
      <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
        Preview
      </Typography>
      <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-secondary)', display: 'block', marginBottom: 16 }}>
        {mode === 'image'
          ? 'Test your image style settings by generating a preview image. This uses AI credits.'
          : mode === 'music'
            ? 'Test your music style settings by generating a preview clip. This uses AI credits.'
            : 'Test your settings by generating a sample response. This uses AI credits.'}
      </Typography>

      <TextField
        label="Sample prompt"
        value={samplePrompt}
        onChange={e => setSamplePrompt(getInputValue(e))}
        style={{ width: '100%', marginBottom: 12 }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleGenerate}
          disabled={isTextPending || !samplePrompt || (isAssetMode && (imagePending || musicPending))}
        >
          {isTextPending
            ? isAssetMode
              ? 'Preparing...'
              : 'Generating...'
            : imagePending
              ? 'Generating image...'
              : musicPending
                ? 'Generating clip...'
                : generateLabel}
        </Button>

        <button
          style={linkButtonStyle}
          onClick={() => setShowPromptPreview(p => !p)}
        >
          {showPromptPreview ? 'Hide prompt' : 'Show prompt'}
        </button>
      </div>

      {showPromptPreview ? (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: 'var(--tuna-palette-action-hover)',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          {previewMessages.map((msg, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <span
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 'var(--tuna-typography-fontWeight-bold)',
                  color: msg.role === 'system' ? 'var(--tuna-palette-info-main)' : 'var(--tuna-palette-success-main)',
                  fontFamily: 'monospace',
                }}
              >
                [{msg.role}]
              </span>{' '}
              <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {error && (
        <Typography variant="caption" style={{ color: 'var(--tuna-palette-error-main)', marginBottom: 8, display: 'block' }}>
          {error}
        </Typography>
      )}

      {isTextPending && (
        <div style={{ height: 4, backgroundColor: 'var(--tuna-palette-action-hover)', borderRadius: 2, marginBottom: 8, overflow: 'hidden' }}>
          <div style={{ width: '30%', height: '100%', backgroundColor: 'var(--tuna-palette-primary-main)', borderRadius: 2, animation: 'progress-indeterminate 1.5s infinite linear' }} />
        </div>
      )}

      {localResult?.text && !isAssetMode && (
        <div
          style={{
            ...boxBorderStyle,
            padding: 16,
            backgroundColor: 'var(--tuna-palette-background-paper)',
            whiteSpace: 'pre-wrap',
            fontSize: '0.875rem',
            lineHeight: 1.6,
          }}
        >
          <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-secondary)', display: 'block', marginBottom: 8 }}>
            Result:
          </Typography>
          {localResult.text}
        </div>
      )}

      {imageUrl && (
        <div style={{ ...boxBorderStyle, overflow: 'hidden', maxWidth: 512 }}>
          <img src={imageUrl} alt="AI generated preview" style={{ width: '100%', display: 'block' }} />
        </div>
      )}

      {musicUrl && (
        <div style={{ ...boxBorderStyle, padding: 16, maxWidth: 512 }}>
          <audio controls src={musicUrl} style={{ width: '100%' }} />
        </div>
      )}

      {isAssetMode && localResult?.text && (
        <>
          <button
            style={{ ...linkButtonStyle, marginBottom: 8 }}
            onClick={() => setShowDescription(p => !p)}
          >
            {showDescription ? 'Hide description' : 'Show description'}
          </button>
          {showDescription ? (
            <div
              style={{
                padding: 12,
                backgroundColor: 'var(--tuna-palette-action-hover)',
                borderRadius: 4,
                whiteSpace: 'pre-wrap',
                fontSize: '0.8rem',
                marginBottom: 16,
              }}
            >
              {localResult.text}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default AIConfigPreview
